<template>
  <!-- The Quest (quest journey 2026-10-27, DESIGN.md + prototype.html; API v1 §3).
       One sky, seven constellations from the bottom up. At a glance: the Next
       card says what's next, the strip of seven glyphs where you are, the lit
       constellations what you have done. Reveals play where they were earned,
       never here; only the Sun's ceremony waits in the Quest. -->
  <div class="quest-panel" :class="{ wide }" data-testid="quest-panel">
    <div class="quest-tabs">
      <button class="quest-tab active">Quest</button>
    </div>

    <div v-if="loading && !j" class="quest-loading"><span class="q-pulse"></span> Gathering your sky…</div>
    <div v-else-if="!j" class="quest-loading">Your sky isn't here yet. Try again in a moment.</div>

    <div v-else class="qp-body">
      <div class="qp-side">
        <!-- The one welcome for money (if they closed the Community's hero before claiming). -->
        <div v-if="j.welcome" class="qp-welcome" data-testid="quest-welcome">
          <div class="qp-eyebrow">{{ j.welcome.title }}</div>
          <div class="qp-amt">{{ j.welcome.total.toLocaleString() }} ✦</div>
          <button class="qp-btn qp-btn-inline" :disabled="claimingAll" data-testid="quest-welcome-claim" @click="claimAll">{{ claimingAll ? '…' : j.welcome.label }}</button>
        </div>
        <div v-else-if="j.claim" class="qp-claimrow" data-testid="quest-stardust">
          <span>Stars waiting for you</span>
          <button class="qp-claim" :disabled="claimingAll" data-testid="quest-claim-all" @click="claimAll">{{ claimingAll ? '…' : `Claim ${claimShown} ✦` }}</button>
        </div>
        <!-- Today's gift star (mystic-page 2026-10-29): one daily row; its ✦ is in the Claim above. -->
        <GiftStarRow v-if="j.giftStar" :star="j.giftStar" :landing="giftLanding" />

        <!-- The Sun: the seal broke; its card waits here and never ambushes. -->
        <div v-if="unsealShown" class="qp-unseal" role="dialog" :aria-label="j.unseal.title" data-testid="quest-unlock">
          <svg viewBox="0 0 84 84" aria-hidden="true" class="qp-unseal-sun"><circle cx="42" cy="42" r="30" fill="url(#qp-unseal-halo)" opacity=".6" />
            <defs><radialGradient id="qp-unseal-halo"><stop offset="0" stop-color="#ffe7b5" stop-opacity=".6" /><stop offset="1" stop-color="#c9a94e" stop-opacity="0" /></radialGradient></defs>
            <circle cx="42" cy="42" r="14" fill="#ffd98a" />
            <line v-for="i in 8" :key="i" :x1="42 + 19 * Math.cos(i * Math.PI / 4)" :y1="42 + 19 * Math.sin(i * Math.PI / 4)" :x2="42 + 30 * Math.cos(i * Math.PI / 4)" :y2="42 + 30 * Math.sin(i * Math.PI / 4)" stroke="#f0d9a3" stroke-width="1.4" /></svg>
          <h3 class="qp-unseal-title">{{ j.unseal.title }}</h3>
          <p v-if="j.unseal.sub" class="qp-unseal-body">{{ j.unseal.sub }}.</p>
          <button class="qp-btn" data-testid="quest-unlock-button" :disabled="claimingStudio" @click="openStudio">{{ claimingStudio ? 'Opening…' : (j.unseal.button ? j.unseal.button.label : 'Open the Studio') }}</button>
          <button class="qp-quiet" data-testid="quest-unlock-later" @click="unsealLater = true">Later</button>
        </div>

        <!-- What's next: one act, its section named in the eyebrow. -->
        <div v-else-if="next && !ceremony" class="qp-next" data-testid="quest-next">
          <div class="qp-eyebrow" data-testid="quest-next-eyebrow"><template v-if="next.section">{{ next.section }}<i class="qp-dot">·</i>Next</template><template v-else>{{ next.eyebrow || 'Next' }}</template></div>
          <h2 class="qp-next-title" data-testid="quest-next-title">{{ next.title }}</h2>
          <p v-if="next.body" class="qp-next-body">{{ next.body }}</p>
          <!-- Say what landed: asked right here (fix 2026-10-30), never a trip to a reading that may not ask. -->
          <div v-if="landing && landing.from === 'next'" class="qp-landed" data-testid="quest-landed-box">
            <textarea ref="landedEl" v-model="landedText" class="qp-note" rows="3" maxlength="2000" :placeholder="LANDED_PROMPT"
                      aria-label="What landed, in your own words" data-testid="quest-landed-text"></textarea>
            <button class="qp-btn" :disabled="acting || !landedText.trim()" data-testid="quest-landed-save" @click="saveLanded">{{ acting ? '…' : 'Save' }}</button>
          </div>
          <button v-else-if="next.button" class="qp-btn" data-testid="quest-next-button" @click="doButton(next.button, 'next')">{{ next.button.label }}</button>
          <p v-if="next.later && !next.button" class="qp-later" data-testid="quest-next-later">{{ next.later }}</p>
          <button v-if="next.quiet" class="qp-quiet" data-testid="quest-next-quiet" @click="doButton(next.quiet, 'quiet')">{{ next.quiet.label }}</button>
        </div>
        <!-- The Mystic's one line back to what landed. -->
        <p v-if="landedAck" class="qp-landed-ack" data-testid="quest-landed-ack">{{ landedAck }}</p>

        <!-- Where am I: seven glyphs. -->
        <div class="qp-strip" role="list" aria-label="Your constellations" data-testid="quest-strip">
          <button v-for="(it, i) in strip.items" :key="it.key" role="listitem" type="button"
                  :class="['qp-sg', it.cls, { bright: ceremony && it.cls === 'done' }]" :style="{ '--d': `${(i * 0.16 + 3.9).toFixed(2)}s` }"
                  :data-testid="`strip-${it.key}`" :aria-label="`${it.name}${it.cls === 'done' ? ', drawn' : it.cls === 'cur' ? ', you are here' : ''}`"
                  @click="centre(it.key, true)">
            <svg viewBox="0 0 32 32" aria-hidden="true" v-html="miniGlyph(it.key, it.on)"></svg>
            <span v-if="it.seed" class="qp-seed" aria-hidden="true"></span>
          </button>
        </div>
        <div class="qp-here" data-testid="quest-here"><template v-if="strip.here">You are in <b>{{ strip.here }}</b></template><template v-else>Every constellation drawn</template></div>

        <!-- The list: beside the sky on a computer; the sky's screen-reader version on a phone. -->
        <ol class="qp-list" :class="{ 'sr-only': !wide }" aria-label="Your quest" data-testid="quest-list">
          <li v-for="r in rows" :key="r.key" :class="`qp-row-${r.type}`">
            <button v-if="r.type === 'section'" type="button" :class="['qp-rb', { fut: r.fut }]" :data-testid="`list-section-${r.key}`" @click="centre(r.key, true)">
              <svg viewBox="0 0 32 32" aria-hidden="true" v-html="miniGlyph(r.key, r.on)"></svg>
              <span class="qp-rn">{{ r.name }}</span><span class="qp-rl">{{ r.line }}</span>
            </button>
            <div v-else-if="r.type === 'sub'" class="qp-sub">{{ r.label }}</div>
            <button v-else type="button" :class="['qp-it', { here: r.here }]" :data-testid="`list-star-${r.key}`" @click="openStar(r.key)">
              <span class="qp-k" aria-hidden="true">{{ r.mark }}</span><span>{{ r.label }}</span><small>{{ r.word || '' }}</small>
              <span class="sr-only">{{ r.mark === '✓' ? '(done)' : r.here ? '(next)' : '' }}</span>
            </button>
          </li>
        </ol>
      </div>

      <div class="qp-skyview" ref="skyView" data-testid="quest-sky-view">
        <QuestSky :journey="j" :fx="fx" :reduced="reduced" :pfx="narrow ? 'qjn' : 'qj'" @star="openStar" />
      </div>

      <!-- A star's sheet: the moment that lit it, or what it asks. -->
      <div v-if="sheet" class="qp-sheet-wrap" data-testid="quest-star-sheet" @click.self="sheet = null">
        <div class="qp-sheet" role="dialog" :aria-label="sheet.title">
          <button class="qp-sheet-x" aria-label="Close" @click="sheet = null">&times;</button>
          <div class="qp-eyebrow">{{ sheet.section }}<template v-if="sheet.along"><i class="qp-dot">·</i>Along the way</template></div>
          <h3 class="qp-sheet-title">{{ sheet.title }}</h3>
          <template v-if="sheet.coming">
            <ul class="qp-coming"><li v-for="c in sheet.coming" :key="c">{{ c }}</li></ul>
            <p class="qp-only">Its rings light when each one arrives.</p>
          </template>
          <template v-else-if="sheet.lit && sheet.moment">
            <div class="qp-mo" data-testid="quest-moment">
              <div v-if="momentDay(sheet.moment.at)" class="qp-mo-date">{{ momentDay(sheet.moment.at) }}</div>
              <div class="qp-mo-line" data-testid="moment-line"><template v-for="(seg, i) in momentSegments(sheet.moment.line)" :key="i"><b v-if="seg.b">{{ seg.text }}</b><i v-else-if="seg.i">{{ seg.text }}</i><template v-else>{{ seg.text }}</template></template></div>
              <p v-if="sheet.moment.quote" class="qp-mo-quote">“{{ sheet.moment.quote }}”</p>
            </div>
            <p v-if="sheet.ring" class="qp-ringline" data-testid="quest-star-ring">{{ sheet.ring.who }} wrote back: <i>{{ sheet.ring.word }}</i></p>
            <p class="qp-only">Only you see this.</p>
          </template>
          <template v-else>
            <p v-if="sheet.body" class="qp-sline">{{ sheet.body }}</p>
            <p v-if="sheet.sub" class="qp-sline qp-sub-line" data-testid="quest-star-sub">{{ sheet.sub }}</p>
          </template>
          <button v-if="sheet.claim" class="qp-btn" data-testid="quest-star-claim" :disabled="!!claiming" @click="claimOne(sheet.claim.key)">{{ claiming ? '…' : `Claim ${sheet.claim.payout} ✦` }}</button>
          <div v-if="landing && landing.from === 'star'" class="qp-landed" data-testid="quest-landed-box">
            <textarea ref="landedEl" v-model="landedText" class="qp-note" rows="3" maxlength="2000" :placeholder="LANDED_PROMPT"
                      aria-label="What landed, in your own words" data-testid="quest-landed-text"></textarea>
            <button class="qp-btn" :disabled="acting || !landedText.trim()" data-testid="quest-landed-save" @click="saveLanded">{{ acting ? '…' : 'Save' }}</button>
          </div>
          <button v-else-if="sheet.button" class="qp-btn" data-testid="quest-star-go" @click="doButton(sheet.button, 'star')">{{ sheet.button.label }}</button>
          <button v-if="sheet.quiet" class="qp-quiet" data-testid="quest-star-quiet" @click="doButton(sheet.quiet, 'star')">{{ sheet.quiet.label }}</button>
        </div>
      </div>

      <!-- "Keep your name and portrait": what the sky gave, kept or changed. -->
      <div v-if="keeping" class="qp-sheet-wrap" data-testid="quest-keep-sheet" @click.self="keeping = false">
        <div class="qp-sheet" role="dialog" aria-label="Your sky gave you a name">
          <button class="qp-sheet-x" aria-label="Close" @click="keeping = false">&times;</button>
          <div class="qp-eyebrow">The Rising</div>
          <h3 class="qp-sheet-title">Your sky gave you a name</h3>
          <div v-if="skyGiftReady" class="qp-gift">
            <img v-if="skyGift.portrait" :src="skyGift.portrait" alt="" class="qp-gift-por" />
            <div class="qp-gift-name" data-testid="quest-sky-name">{{ skyGift.name }}</div>
          </div>
          <p v-else class="qp-sline">Your name and portrait are still being painted from your chart. They'll be here soon.</p>
          <p class="qp-sline">Keep them, or change either, any time.</p>
          <button class="qp-btn" :disabled="acting || !skyGiftReady" data-testid="quest-keep-sky" @click="keepSky">{{ acting ? '…' : 'Keep them' }}</button>
          <button class="qp-quiet" data-testid="quest-change-sky" @click="keeping = false; emitAct({ type: 'mirror' })">Change either</button>
        </div>
      </div>

      <!-- "Say thank you" to the Mystic of their own reading (Mystic AIda counts). -->
      <div v-if="thanking" class="qp-sheet-wrap" data-testid="quest-thank-sheet" @click.self="thanking = null">
        <div class="qp-sheet" role="dialog" aria-label="Say thank you">
          <button class="qp-sheet-x" aria-label="Close" @click="thanking = null">&times;</button>
          <div class="qp-eyebrow">Venus</div>
          <h3 class="qp-sheet-title">Say thank you</h3>
          <p class="qp-sline">Tell Mystic AIda what the reading meant.</p>
          <div class="qp-chips">
            <button v-for="w in THANKS" :key="w" type="button" :class="['qp-chip', { on: thankNote === w }]" @click="thankNote = w">{{ w }}</button>
          </div>
          <textarea v-model="thankNote" class="qp-note" maxlength="280" rows="2" placeholder="Or in your own words" aria-label="Your thanks"></textarea>
          <button class="qp-btn" :disabled="acting || !thankNote.trim()" data-testid="quest-thank-send" @click="sendThanks">{{ acting ? '…' : 'Send thanks' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { store } from '@/store'
import { getPlatformQuests, claimQuest, claimAllQuests, navSeen, questAct } from '@/api-service'
import GiftStarRow from '@/components/quest/GiftStarRow.vue'
import { GIFT_STAR_SEEN_KEY } from '@/utils/quest-sky'
import { track } from '@/utils/analytics'
import { readJourney, nextCard, stripItems, listRows, starSheet, actionFor, findStar, sectionCentre, sectionKey, miniGlyph,
  momentSegments, momentDay, skyLayout, SKY_W } from '@/utils/quest-sky'
import QuestSky from '@/components/quest/QuestSky.vue'

const props = defineProps({
  // ?panel=quest&star=<key>: centre that star and ring it once.
  star: { type: String, default: '' },
  stage: { type: Number, default: 0 },
  // Hosted in the room's 640px sheet: the phone layout at any width.
  narrow: { type: Boolean, default: false },
  // nav.sky_gift (API §5): the name and round portrait the birthday made.
  skyGift: { type: Object, default: null },
})
const emit = defineEmits(['close', 'go-to-studio', 'navigate', 'act', 'unclaimed', 'mystic-unlocked', 'entry-carats', 'nav'])

const THANKS = ['Exactly right', 'It helped', 'I needed that']
const LANDED_PROMPT = 'A line in your own words about what stayed with you.'
const CEREMONY_MS = 5600
const CEREMONY_REDUCED_MS = 1000

const loading = ref(true)
const raw = ref(null)
const skyView = ref(null)
const wide = ref(false)
const reduced = ref(false)
const sheet = ref(null)
const keeping = ref(false)
const thanking = ref(null)
const thankNote = ref('')
// Say what landed, asked in place: { from: 'next' | 'star', reading, … } while open.
const landing = ref(null)
const landedText = ref('')
const landedAck = ref('')
const landedEl = ref(null)
const acting = ref(false)
const claiming = ref('')
const claimingAll = ref(false)
const claimingStudio = ref(false)
const unsealLater = ref(false)
const ceremony = ref(false)
const fx = ref({})
let timers = []
const later = (ms, fn) => { const t = setTimeout(fn, ms); timers.push(t); return t }

const portrait = computed(() => store.querent_avatar_url || (props.skyGift && props.skyGift.portrait) || '')
const j = computed(() => (raw.value ? readJourney(raw.value, { portrait: portrait.value }) : null))
const next = computed(() => (j.value ? nextCard(j.value) : null))
const strip = computed(() => (j.value ? stripItems(j.value) : { items: [], here: '' }))
const rows = computed(() => (j.value ? listRows(j.value) : []))
const unsealShown = computed(() => !!(j.value && j.value.unseal && !unsealLater.value && !ceremony.value))
const skyGiftReady = computed(() => !!(props.skyGift && props.skyGift.state === 'ready' && props.skyGift.name))

function prefersReduced () {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) } catch (e) { return false }
}
function measureWide () {
  try { wide.value = !props.narrow && window.innerWidth >= 900 } catch (e) { wide.value = false }
}

/** Scroll the sky so a section (or y) sits mid-view. */
function scrollToY (y, smooth = false) {
  const v = skyView.value
  if (!v || y == null) return
  const sc = v.clientWidth ? v.clientWidth / SKY_W : 1
  const top = Math.max(0, y * sc - v.clientHeight * 0.5)
  try { v.scrollTo({ top, behavior: smooth && !reduced.value ? 'smooth' : 'auto' }) } catch (e) { v.scrollTop = top }
}
function centre (k, smooth = false) { scrollToY(sectionCentre(k || (j.value && j.value.cur) || 'su', j.value), smooth) }
function starY (key) {
  const hit = findStar(j.value, key)
  if (!hit || hit.star.y == null) return null
  return skyLayout().top[hit.sec.key] + hit.star.y
}

function apply (d, how = 'step') {
  if (!d || !d.sky) return
  if (typeof d.mystic_entry_carats === 'number') emit('entry-carats', d.mystic_entry_carats)
  raw.value = d.sky
  const J = j.value
  emit('unclaimed', J && J.money && J.claim ? J.claim.total : (J && J.welcome ? J.welcome.total : 0))
  nextTick(() => {
    if (how !== 'open') return
    if (props.star && findStar(J, props.star)) {
      scrollToY(starY(props.star))
      fx.value = { ring: props.star }
      later(1800, () => { fx.value = {} })
    } else centre(J && J.cur)
    playOpenMoments(J)
  })
}

// The gift star lands once (the visit after a gift was opened): the star
// ignites and its ✦ rises into the Claim, which counts up to the total. With
// reduced motion it is simply lit and the total is already there.
const giftLanding = ref(false)
const claimShown = computed(() => {
  const J = j.value
  if (!J || !J.claim) return 0
  return giftLanding.value && J.giftStar ? Math.max(0, J.claim.total - J.giftStar.carats) : J.claim.total
})
function landGiftStar (J) {
  const g = J && J.giftStar
  if (!g || g.state !== 'lit' || !g.litAt) return
  let seen = ''
  try { seen = localStorage.getItem(GIFT_STAR_SEEN_KEY) || '' } catch (e) { /* */ }
  if (seen === g.litAt) return
  try { localStorage.setItem(GIFT_STAR_SEEN_KEY, g.litAt) } catch (e) { /* */ }
  if (reduced.value) return
  giftLanding.value = true
  later(1900, () => { giftLanding.value = false })
}

/** On opening: the Sun's ceremony when it is due here; else a reply rings a star once. */
function playOpenMoments (J) {
  if (!J) return
  landGiftStar(J)
  const u = J.unlock
  if (u && sectionKey(u.constellation) === 'su' && J.unseal) { playCeremony(); return }
  const ring = J.rings[0]
  if (ring) {
    const y = starY(ring.star)
    if (y != null) scrollToY(y)
    later(reduced.value ? 100 : 900, () => { fx.value = { ring: ring.star, liftWord: ring.word } })
    later(4200, () => { fx.value = { ringDone: ring.star } })
  }
}

/** The Sun rises and breaks the seal; every drawn constellation brightens; then its card. */
function playCeremony () {
  ceremony.value = true
  centre('su')
  later(reduced.value ? 200 : 500, () => { fx.value = { ceremony: true, bright: true } })
  later(reduced.value ? CEREMONY_REDUCED_MS : CEREMONY_MS, () => {
    ceremony.value = false
    fx.value = {}
    track('nav_unlock_seen', { key: 'the_sun' })
    seen('unlock:the_sun')
  })
}

async function seen (key) {
  if (!store.session_id) return
  try {
    const r = await navSeen(store.session_id, key)
    if (r && r.nav) emit('nav', r.nav)
  } catch (e) { /* the moment waits for next time */ }
}

function emitAct (verb) { emit('act', verb) }

/** A button's verb: some are done here (the acts, the claim, the Studio), the rest by ReaderPage. */
function doButton (button, from) {
  const verb = actionFor(button)
  if (!verb) return
  if (from === 'next') track('nav_next', { star: (next.value && next.value.star) || '', action: button.kind, stage: props.stage })
  landedAck.value = ''
  if (verb.type === 'landed') { openLanded(verb, from); return }
  sheet.value = null
  if (verb.type === 'act') { runAct(verb.act); return }
  if (verb.type === 'keep-sky') { keeping.value = true; return }
  if (verb.type === 'thank-mystic') { thanking.value = verb; thankNote.value = ''; return }
  if (verb.type === 'claim-all') { claimAll(); return }
  if (verb.type === 'claim-studio') { openStudio(); return }
  if (verb.type === 'studio') { emit('go-to-studio', verb); return }
  if (verb.type === 'quest') { emit('navigate', { category: verb.category, panel: 'altar' }); return }
  emitAct(verb)
}

async function runAct (act, extra = {}) {
  if (acting.value || !store.session_id) return null
  acting.value = true
  try {
    const r = await questAct(store.session_id, act, extra)
    if (r && r.nav) emit('nav', r.nav)
    if (r && r.sky) apply({ sky: r.sky })
    return r
  } catch (e) { return null } finally { acting.value = false }
}
async function keepSky () {
  const r = await runAct('keep_sky')
  if (r && r.ok !== false) keeping.value = false
}
async function sendThanks () {
  const t = thanking.value
  if (!t) return
  const r = await runAct('thank_mystic', { reading_id: t.reading, note: thankNote.value.trim() })
  if (r && r.ok !== false) thanking.value = null
}

function openLanded (verb, from) {
  landing.value = { ...verb, from: from === 'star' ? 'star' : 'next' }
  landedText.value = ''
  nextTick(() => { try { if (landedEl.value) landedEl.value.focus() } catch (e) { /* */ } })
}
async function saveLanded () {
  const text = landedText.value.trim()
  if (!text || !landing.value) return
  const r = await runAct('landed', { text })
  // Not kept (offline, a 500): the box and their words stay.
  if (!r || r.ok === false) return
  if (landing.value && landing.value.from === 'star') sheet.value = null
  landing.value = null
  landedText.value = ''
  landedAck.value = String(r.line || '').trim()
}

// The sheet closed or moved to another star: its box goes with it.
watch(sheet, () => { if (landing.value && landing.value.from === 'star') landing.value = null })

function openStar (key) {
  const s = starSheet(j.value, key)
  if (s) sheet.value = s
}

async function load (how = 'step') {
  if (!store.session_id) { loading.value = false; return }
  loading.value = true
  try { apply(await getPlatformQuests(store.session_id), how) } catch (e) { /* */ } finally { loading.value = false }
}

async function claimOne (key) {
  if (claiming.value) return
  claiming.value = key
  try {
    const res = await claimQuest(store.session_id, key)
    if (res && typeof res.balance === 'number') store.querent_carat_balance = res.balance
    apply(res)
    sheet.value = null
  } catch (e) { /* */ } finally { claiming.value = '' }
}
async function claimAll () {
  if (claimingAll.value) return
  claimingAll.value = true
  try {
    const res = await claimAllQuests(store.session_id)
    if (res && typeof res.balance === 'number') store.querent_carat_balance = res.balance
    apply(res)
  } catch (e) { /* */ } finally { claimingAll.value = false }
}

// The Studio is claimed, not handed over: ReaderPage records it and opens the
// Studio entry (its price shown as a goal), then calls studioAccepted().
function openStudio () {
  if (claimingStudio.value) return
  claimingStudio.value = true
  emit('mystic-unlocked')
}
function studioAccepted () {
  claimingStudio.value = false
  load()
}

watch(() => store.session_id, (id, old) => { if (id && !old) load('open') })

onMounted(async () => {
  reduced.value = prefersReduced()
  measureWide()
  window.addEventListener('resize', measureWide)
  await load('open')
  // Opening the Quest clears its dot and makes the fresh rings old.
  if (raw.value) seen('quest')
})
onUnmounted(() => {
  timers.forEach(clearTimeout)
  timers = []
  window.removeEventListener('resize', measureWide)
})
defineExpose({ load, studioAccepted, sky: j })
</script>

<style scoped>
.quest-panel { width: 100%; height: 100%; background: radial-gradient(130% 55% at 50% 0%, #1f103e 0%, #0d0520 62%); color: #efe4ff; padding: 0 16px 84px;
  box-sizing: border-box; display: flex; flex-direction: column; overflow: hidden; }
.quest-panel *, .quest-panel *::before, .quest-panel *::after { box-sizing: border-box; }
.quest-tabs { display: flex; justify-content: center; border-bottom: 1px solid rgba(155,134,201,.18); padding: 8px 16px 0; margin: 0 0 10px; flex: 0 0 auto; }
.quest-tab { position: relative; padding: 0 22px 7px; background: none; border: none; border-bottom: 2px solid #f0d9a3; font: 600 14px Cinzel, serif; letter-spacing: .16em; color: #f0d9a3; cursor: default; }
.quest-panel.wide .quest-tabs { display: none; }
.quest-tab::after { content: ''; position: absolute; inset: -10px 0; }
.quest-loading { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 40px 0; color: #9b86c9; font-size: 13px; }
.q-pulse { width: 10px; height: 10px; border-radius: 50%; background: #c9a94e; animation: qp-pulse 1.2s ease-in-out infinite; }
@keyframes qp-pulse { 0%, 100% { opacity: .3 } 50% { opacity: 1 } }

.qp-body { position: relative; flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; gap: 10px; max-width: 520px; width: 100%; margin: 0 auto; }
.qp-side { flex: 0 0 auto; display: flex; flex-direction: column; gap: 0; position: relative; }
.qp-skyview { position: relative; flex: 1 1 auto; min-height: 220px; overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; scrollbar-width: none;
  border: 1px solid rgba(201,169,78,.35); border-radius: 20px; background: radial-gradient(120% 70% at 50% 100%, #22114a 0%, #120829 60%, #0b0419 100%); }
.qp-skyview::-webkit-scrollbar { display: none; }
.quest-panel.wide { padding-top: 10px; }
.quest-panel.wide .qp-body { max-width: 960px; flex-direction: row-reverse; justify-content: center; align-items: stretch; gap: 28px; }
.quest-panel.wide .qp-side { flex: 0 0 380px; overflow-y: auto; scrollbar-width: thin; }
.quest-panel.wide .qp-skyview { flex: 0 1 500px; }

.qp-eyebrow { font: 600 10px Cinzel, serif; letter-spacing: .2em; text-transform: uppercase; color: #f0d9a3; }
.qp-dot { font-style: normal; color: #8e7bb5; margin: 0 4px; }
.qp-next { border: 1px solid rgba(201,169,78,.45); border-radius: 18px; padding: 12px 16px 13px; background: linear-gradient(170deg, #28164a, #170b2e); }
.qp-next-title { font: 500 22px/1.12 'Cormorant Garamond', Cormorant, serif; margin: 4px 0 3px; color: #fff; }
.quest-panel.wide .qp-next-title { font-size: 23px; }
.qp-next-body { margin: 0 0 9px; color: #b9a6d6; font-size: 13px; line-height: 1.45; }
.qp-later { margin: 2px 0 0; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qp-btn { display: block; width: 100%; min-height: 44px; border: 0; border-radius: 14px; padding: 12px 14px; cursor: pointer; background: linear-gradient(#f3dfa8, #c9a94e);
  color: #22133b; font: 600 13px Cinzel, serif; letter-spacing: .1em; text-transform: uppercase; box-shadow: 0 6px 18px rgba(201,169,78,.25); }
.qp-btn:disabled { opacity: .6; }
.qp-btn:focus-visible, .qp-quiet:focus-visible, .qp-sg:focus-visible { outline: 2px solid #ffe7b5; outline-offset: 2px; }
.qp-btn-inline { display: inline-block; width: auto; padding: 9px 26px; }
.qp-quiet { display: block; margin: 8px auto 0; background: none; border: 0; color: #b9a6d6; font: italic 16px 'Cormorant Garamond', Cormorant, serif;
  text-decoration: underline; text-underline-offset: 3px; cursor: pointer; min-height: 32px; }

.qp-welcome { margin-bottom: 10px; border-radius: 18px; padding: 13px 16px; text-align: center; border: 1px solid rgba(240,217,163,.5);
  background: radial-gradient(120% 120% at 50% 0%, rgba(240,217,163,.16), rgba(240,217,163,.03) 70%); }
.qp-amt { font: 500 34px/1.1 'Cormorant Garamond', Cormorant, serif; color: #ffe7b5; margin: 2px 0 8px; }
.qp-claimrow { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; padding: 8px 10px 8px 12px; border-radius: 12px;
  border: 1px solid rgba(201,169,78,.35); background: rgba(20,8,40,.72); font: italic 15px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-claim { flex: 0 0 auto; min-height: 36px; padding: 6px 12px; border: none; border-radius: 9px; cursor: pointer; font: 600 13px Cinzel, serif; color: #1a0f2e;
  background: linear-gradient(180deg, #f0d9a3, #c9a94e); }

.qp-unseal { text-align: center; padding: 16px 20px 14px; border-radius: 22px; background: radial-gradient(120% 90% at 50% 0%, #2b1752, #150a2b);
  border: 1px solid rgba(240,217,163,.55); box-shadow: 0 0 50px rgba(240,217,163,.16); animation: qp-rise .7s cubic-bezier(.2,.8,.2,1) both; }
.qp-unseal-sun { width: 64px; height: 64px; }
.qp-unseal-title { font: 500 27px/1.1 'Cormorant Garamond', Cormorant, serif; margin: 4px 0; color: #fff; }
.qp-unseal-body { color: #b9a6d6; margin: 0 0 12px; font-size: 14px; }
@keyframes qp-rise { from { transform: translateY(24px); opacity: 0; } }

/* The strip: seven constellations at a glance. */
.qp-strip { display: flex; justify-content: space-between; align-items: center; margin: 10px 2px 0; position: relative; }
.qp-strip::before { content: ''; position: absolute; left: 14px; right: 14px; top: 50%; border-top: 1px dashed rgba(155,134,201,.35); }
.qp-sg { position: relative; width: 38px; height: 38px; border-radius: 50%; border: 1px solid rgba(155,134,201,.25); background: #120829; display: grid; place-items: center; padding: 0; cursor: pointer; }
.qp-sg svg { width: 26px; height: 26px; }
/* A 44px touch target around the 38px glyph (e2e round 2). */
.qp-sg::after { content: ''; position: absolute; inset: -4px; border-radius: 50%; }
.qp-sg.done { border-color: rgba(240,217,163,.55); background: #1d0f38; }
.qp-sg.cur { border: 1.5px solid #ffe7b5; box-shadow: 0 0 0 4px rgba(240,217,163,.12), 0 0 14px rgba(240,217,163,.35); }
.qp-sg.fut { opacity: .45; }
.qp-sg.fut:has(.qp-seed) { opacity: .8; }
.qp-seed { position: absolute; top: 3px; right: 3px; width: 7px; height: 7px; border-radius: 50%; background: #ffe7b5; box-shadow: 0 0 6px #ffe7b5; }
.qp-sg.bright { animation: qp-bright 1.1s ease-out both; animation-delay: var(--d); }
@keyframes qp-bright { 0% { box-shadow: 0 0 0 0 rgba(255,231,181,0); } 40% { box-shadow: 0 0 22px 8px rgba(255,231,181,.75); background: #3a2560; } 100% { box-shadow: 0 0 0 0 rgba(255,231,181,0); } }
.qp-here { text-align: center; font: italic 15px 'Cormorant Garamond', Cormorant, serif; color: #b9a6d6; margin-top: 4px; }
.qp-here b { font: 600 10.5px Cinzel, serif; letter-spacing: .16em; color: #f0d9a3; text-transform: uppercase; }

/* The list (desktop). */
.qp-list { list-style: none; margin: 14px 0 0; padding: 0; }
.qp-rb { width: 100%; display: flex; align-items: center; gap: 10px; background: none; border: 0; border-bottom: 1px solid rgba(155,134,201,.16); padding: 9px 2px; text-align: left; cursor: pointer; color: inherit; }
.qp-rb svg { width: 22px; height: 22px; flex: none; }
.qp-rn { font: 600 13px Cinzel, serif; letter-spacing: .08em; color: #f0d9a3; white-space: nowrap; }
.qp-rb.fut .qp-rn { color: #9b86c9; opacity: .6; }
.qp-rl { flex: 1; text-align: right; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-rb.fut .qp-rl { color: #8e7bb5; }
.qp-it { width: 100%; display: grid; grid-template-columns: 22px 1fr auto; gap: 0 6px; align-items: baseline; background: none; border: 0; padding: 6px 2px 6px 32px; text-align: left;
  font: 400 16px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; cursor: pointer; }
.qp-it:hover, .qp-rb:hover { background: rgba(155,134,201,.08); }
.qp-k { color: #f0d9a3; font-size: 13px; }
.qp-it.here { color: #fff; font-weight: 600; }
.qp-it small { font: italic 15px 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qp-sub { font: 600 9.5px Cinzel, serif; letter-spacing: .18em; color: #8e7bb5; text-transform: uppercase; padding: 8px 0 0 54px; }

/* Sheets. */
.qp-sheet-wrap { position: fixed; inset: 0; z-index: 1000; display: flex; align-items: flex-end; justify-content: center; background: rgba(5,2,12,.5); }
.quest-panel.wide .qp-sheet-wrap { align-items: center; }
.qp-sheet { position: relative; width: 100%; max-width: 520px; max-height: 80%; overflow-y: auto; background: linear-gradient(180deg, #22113f, #160a2c);
  border-radius: 26px 26px 0 0; border-top: 1px solid rgba(201,169,78,.55); padding: 18px 20px calc(26px + 70px); animation: qp-up .34s cubic-bezier(.2,.8,.2,1) both; }
.quest-panel.wide .qp-sheet { width: 460px; border-radius: 22px; border: 1px solid rgba(201,169,78,.45); padding-bottom: 26px; }
@keyframes qp-up { from { transform: translateY(40px); opacity: 0; } }
.qp-sheet-x { position: absolute; top: 10px; right: 12px; width: 36px; height: 36px; border: 0; background: none; color: #b9a6d6; font-size: 24px; line-height: 1; cursor: pointer; }
.qp-sheet-title { font: 500 25px/1.15 'Cormorant Garamond', Cormorant, serif; margin: 4px 34px 10px 0; color: #fff; }
.qp-sline { margin: 0 0 12px; font: 400 18px/1.35 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-sub-line { font-style: italic; color: #f0d9a3; }
.qp-mo { border-radius: 16px; padding: 13px 15px 14px; margin: 4px 0 10px; background: radial-gradient(120% 120% at 0% 0%, rgba(240,217,163,.09), rgba(240,217,163,.02) 60%); border: 1px solid rgba(240,217,163,.2); }
.qp-mo-date { font: 600 9.5px Cinzel, serif; letter-spacing: .18em; color: #8e7bb5; text-transform: uppercase; margin-bottom: 5px; }
.qp-mo-line { font: 400 20px/1.32 'Cormorant Garamond', Cormorant, serif; color: #efe4ff; }
.qp-mo-line i { color: #f0d9a3; }
.qp-mo-line b { font-weight: 600; color: #fff; }
.qp-mo-quote { margin: 9px 0 0; padding: 2px 0 2px 12px; border-left: 2px solid rgba(201,169,78,.6); font: italic 19px/1.3 'Cormorant Garamond', Cormorant, serif; color: #ffe7b5; }
.qp-ringline { margin: 0 0 8px; font: 400 17px 'Cormorant Garamond', Cormorant, serif; color: #e6dafa; }
.qp-ringline i { color: #ffe7b5; }
.qp-only { margin: 10px 0 0; text-align: center; font: italic 14px 'Cormorant Garamond', Cormorant, serif; color: #8e7bb5; }
.qp-coming { list-style: none; padding: 0; margin: 4px 0 0; display: flex; flex-wrap: wrap; gap: 6px; }
.qp-coming li { border: 1px dashed rgba(155,134,201,.5); border-radius: 99px; padding: 4px 11px; font: italic 16px 'Cormorant Garamond', Cormorant, serif; color: #b9a6d6; }
.qp-gift { display: flex; flex-direction: column; align-items: center; gap: 6px; margin: 4px 0 12px; }
.qp-gift-por { width: 92px; height: 92px; border-radius: 50%; object-fit: cover; box-shadow: 0 0 0 2px #f0d9a3; }
.qp-gift-name { font: 500 30px 'Cormorant Garamond', Cormorant, serif; color: #fff; }
.qp-chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 8px; }
.qp-chip { border: 1px solid rgba(240,217,163,.4); background: none; color: #f0d9a3; border-radius: 99px; padding: 6px 12px; font: italic 16px 'Cormorant Garamond', Cormorant, serif; cursor: pointer; }
.qp-chip.on { background: rgba(240,217,163,.15); }
.qp-landed { margin: 2px 0 0; }
.qp-landed-ack { margin: 10px 2px 0; font: italic 17px/1.4 'Cormorant Garamond', Cormorant, serif; color: #f0d9a3; }
.qp-note { width: 100%; margin: 0 0 4px; border-radius: 12px; border: 1px solid rgba(155,134,201,.35); background: rgba(10,4,24,.6); color: #efe4ff; padding: 8px 10px; font: 400 16px 'Cormorant Garamond', Cormorant, serif; resize: vertical; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
@media (prefers-reduced-motion: reduce) {
  .qp-unseal, .qp-sheet { animation: qp-fade .4s ease-out both; }
  .qp-sg.bright { animation: none; }
}
@keyframes qp-fade { from { opacity: 0; } }
</style>
